import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { StandardSectionExtractButton } from './StandardSectionExtractButton'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ExtractTranslationLocalesField } from './ExtractTranslationLocalesField'
import { StandardSectionExtractStepper } from './StandardSectionExtractStepper'
import {
  getExtractionPreviewUiState,
  StandardExtractionPreviewContent,
} from './StandardExtractionPreviewContent'
import { StandardExtractErrorAlert } from './StandardExtractErrorAlert'
import { resolveExtractDialogTitle } from './standardSectionExtractDialogTitles'
import { preventExtractDialogOutsideDismiss } from './standardSectionExtractDialogProps'
import type { StandardSectionExtractionState } from '../hooks/useStandardSectionExtraction'

type StandardSectionExtractDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  extraction: StandardSectionExtractionState
}

export function StandardSectionExtractDialog({
  open,
  onOpenChange,
  extraction,
}: StandardSectionExtractDialogProps) {
  const { t } = useTranslation('standardsLibrary')
  const {
    section,
    sectionNotFoundFlow,
    skippedDialogSteps,
    dialogSteps,
    dialogStep,
    translationLocales,
    setTranslationLocales,
    previewData,
    extractErrorMessage,
    translateProgressMessage,
    runExtractAndOpenPreview,
    runTranslateStream,
    skipTranslate,
    handleSave,
    advanceFromReview,
    advanceFromAcknowledgeNotFound,
    goToSaveStep,
    goBack,
    isExtracting,
    isTranslating,
    isApplying,
    sectionSourceLocale,
  } = extraction

  const previewUiState =
    previewData != null
      ? getExtractionPreviewUiState(section, previewData, t)
      : null

  const dialogTitle = resolveExtractDialogTitle(dialogStep, section, t)
  const isBusy = isExtracting || isTranslating || isApplying
  const showExtractError = extractErrorMessage != null && extractErrorMessage !== ''

  // acknowledgeNotFound is a sub-state of the extract step — hide it from the stepper
  const stepperSteps = dialogSteps.filter((step) => step !== 'acknowledgeNotFound')
  const stepperCurrentStep = dialogStep === 'acknowledgeNotFound' ? 'extract' : dialogStep
  const stepperSkippedSteps = skippedDialogSteps.filter((step) => step !== 'acknowledgeNotFound')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='flex h-[680px] max-w-4xl! flex-col gap-0 overflow-hidden p-0'
        {...preventExtractDialogOutsideDismiss}
      >
        <DialogHeader className='shrink-0 border-b px-6 py-4'>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>
        <div className='flex min-h-0 flex-1'>
          {/* Left column — vertical stepper */}
          <div className='w-52 shrink-0 overflow-y-auto border-r px-5 py-6'>
            <StandardSectionExtractStepper
              steps={stepperSteps}
              currentStep={stepperCurrentStep}
              skippedSteps={stepperSkippedSteps}
              orientation='vertical'
            />
          </div>
          {/* Right column — step content + footer */}
          <div className='flex min-h-0 flex-1 flex-col'>
            <div className='flex-1 space-y-4 overflow-y-auto px-6 py-4'>
              {dialogStep === 'extract' && (
                <>
                  <p className='text-sm text-muted-foreground'>
                    {t('detail.extractDialog.extractStepDescription')}
                  </p>
                  {showExtractError && (
                    <StandardExtractErrorAlert message={extractErrorMessage} />
                  )}
                </>
              )}
              {dialogStep === 'acknowledgeNotFound' && previewData != null && (
                <>
                  <p className='text-sm text-muted-foreground'>
                    {t('detail.extractDialog.acknowledgeNotFoundDescription')}
                  </p>
                  <StandardExtractionPreviewContent
                    section={section}
                    preview={previewData}
                    contentVariant='source'
                  />
                </>
              )}
              {dialogStep === 'review' && previewData != null && (
                <StandardExtractionPreviewContent
                  section={section}
                  preview={previewData}
                  contentVariant='source'
                />
              )}
              {dialogStep === 'translate' && (
                <>
                  <p className='text-sm text-muted-foreground'>
                    {t('detail.extractDialog.translateStepDescription')}
                  </p>
                  <ExtractTranslationLocalesField
                    value={translationLocales}
                    onChange={setTranslationLocales}
                    sourceLocale={sectionSourceLocale}
                    disabled={isTranslating}
                  />
                  {translateProgressMessage != null && (
                    <p className='flex items-center gap-2 text-sm text-muted-foreground'>
                      <Loader2 className='size-4 shrink-0 animate-spin' aria-hidden />
                      {translateProgressMessage}
                    </p>
                  )}
                </>
              )}
              {dialogStep === 'reviewTranslations' && previewData != null && (
                <>
                  <p className='text-sm text-muted-foreground'>
                    {t('detail.extractDialog.reviewTranslationsDescription')}
                  </p>
                  <StandardExtractionPreviewContent
                    section={section}
                    preview={previewData}
                    contentVariant='display'
                  />
                </>
              )}
              {dialogStep === 'save' && (
                <>
                  {sectionNotFoundFlow && previewData != null ? (
                    <StandardExtractionPreviewContent
                      section={section}
                      preview={previewData}
                      contentVariant='source'
                    />
                  ) : (
                    <p className='text-sm text-muted-foreground'>
                      {t('detail.extractDialog.saveStepDescription')}
                    </p>
                  )}
                </>
              )}
            </div>
            <DialogFooter className='shrink-0 border-t px-6 py-4'>
              {dialogStep === 'extract' && (
                <>
                  <Button
                    variant='outline'
                    onClick={() => onOpenChange(false)}
                    disabled={isExtracting}
                  >
                    {t('detail.extractPreview.cancel')}
                  </Button>
                  <StandardSectionExtractButton
                    extraction={extraction}
                    onClick={() => void runExtractAndOpenPreview()}
                    labelOverride={
                      showExtractError
                        ? t('detail.extractDialog.retryExtract')
                        : undefined
                    }
                  />
                </>
              )}
              {dialogStep === 'acknowledgeNotFound' && (
                <>
                  <Button variant='outline' onClick={goBack} disabled={isBusy}>
                    {t('detail.extractDialog.back')}
                  </Button>
                  <Button onClick={advanceFromAcknowledgeNotFound} disabled={isBusy}>
                    {isApplying
                      ? t('detail.extractPreview.applying')
                      : t('detail.extractDialog.save')}
                  </Button>
                </>
              )}
              {dialogStep === 'review' && (
                <>
                  <Button variant='outline' onClick={goBack} disabled={isBusy}>
                    {t('detail.extractDialog.back')}
                  </Button>
                  <Button
                    onClick={advanceFromReview}
                    disabled={isBusy || previewUiState?.canProceed !== true}
                  >
                    {t('detail.extractDialog.continue')}
                  </Button>
                </>
              )}
              {dialogStep === 'translate' && (
                <>
                  <Button variant='outline' onClick={goBack} disabled={isTranslating}>
                    {t('detail.extractDialog.back')}
                  </Button>
                  <Button
                    variant='outline'
                    onClick={skipTranslate}
                    disabled={isTranslating}
                  >
                    {t('detail.extractDialog.skipTranslation')}
                  </Button>
                  <Button
                    onClick={() => void runTranslateStream()}
                    disabled={isTranslating || translationLocales.length === 0}
                  >
                    {isTranslating ? (
                      <>
                        <Loader2 className='mr-2 size-4 animate-spin' aria-hidden />
                        {t('detail.extractDialog.translating')}
                      </>
                    ) : (
                      t('detail.extractDialog.startTranslation')
                    )}
                  </Button>
                </>
              )}
              {dialogStep === 'reviewTranslations' && (
                <>
                  <Button variant='outline' onClick={goBack} disabled={isBusy}>
                    {t('detail.extractDialog.back')}
                  </Button>
                  <Button
                    onClick={goToSaveStep}
                    disabled={isBusy || previewUiState?.canProceed !== true}
                  >
                    {t('detail.extractDialog.continueToSave')}
                  </Button>
                </>
              )}
              {dialogStep === 'save' && (
                <>
                  <Button variant='outline' onClick={goBack} disabled={isApplying}>
                    {t('detail.extractDialog.back')}
                  </Button>
                  <Button
                    onClick={() => void handleSave()}
                    disabled={isApplying || previewUiState?.canProceed !== true}
                  >
                    {isApplying
                      ? t('detail.extractPreview.applying')
                      : t('detail.extractDialog.save')}
                  </Button>
                </>
              )}
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
